import React, { useRef } from 'react';
import {
  FaFileUpload,
  FaEye,
  FaCheckCircle,
} from 'react-icons/fa';
import { SectionDefinitionCard } from '../SectionDefinitionCard';
import { SectionsTableCard } from '../SectionsTableCard';
import ExtractionModal from './ExtractionModal';
import { styles } from './styles';
import { 
  handleFileUpload, 
  handleExtractData,
  calculateFilesWithSections,
  calculateFilesWithoutSections 
} from './utils/uploadContentUtils';

const ExtractionFlow = ({
  extractionFiles,
  setExtractionFiles,
  sections,
  setSections,
  currentSection,
  setCurrentSection,
  editingSectionId,
  setEditingSectionId,
  isProcessing,
  setIsProcessing,
  showExtractionFiles,
  setShowExtractionFiles,
  titleOptions,
  periodTypeOptions,
  financialStatementsData,
  onExtractionComplete,
  removeFile,
  // Section management handlers
  handleInputChange,
  handleCompanyInputChange,
  handleTickerInputChange,
  handleTitleSelection,
  handlePeriodTypeSelection,
  handleCancelEdit,
  handleUpdateSection,
  handleAddSection,
  handleEditSection,
  handleDeleteSection,
  handleClearSections
}) => {
  const extractionFileInputRef = useRef(null);

  const handleFileUploadWrapper = (files) => {
    const newFiles = handleFileUpload(files, extractionFiles, true);
    if (newFiles.length > 0) {
      setExtractionFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    handleFileUploadWrapper(e.dataTransfer.files);
  };

  const handleExtractDataWrapper = () => {
    handleExtractData(
      extractionFiles,
      sections,
      financialStatementsData,
      setIsProcessing,
      onExtractionComplete
    );
  };

  const filesWithSections = calculateFilesWithSections(sections, extractionFiles);
  const filesWithoutSections = calculateFilesWithoutSections(extractionFiles, filesWithSections);

  const handleRemoveFile = (id) => {
    removeFile(id, true);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Column - File Upload */}
      <div className="lg:col-span-1">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          {/* Upload Container */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200">
                Upload PDF Files
              </label>
              {extractionFiles.length > 0 && (
                <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full">
                  {extractionFiles.length} uploaded
                </span>
              )}
            </div>
            
            <div
              onDrop={handleFileDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => extractionFileInputRef.current.click()}
              className={styles.extractionUploadBox(extractionFiles.length > 0)}
            >
              <input
                ref={extractionFileInputRef}
                type="file"
                className="hidden"
                accept=".pdf"
                multiple
                onChange={(e) => handleFileUploadWrapper(e.target.files)}
              />

              <div className={`p-4 rounded-full inline-flex mb-4 ${
                extractionFiles.length > 0 
                  ? 'bg-green-100 dark:bg-green-900/20' 
                  : 'bg-purple-100 dark:bg-purple-900/20'
              }`}>
                <FaFileUpload className={`text-2xl ${
                  extractionFiles.length > 0 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-purple-600 dark:text-purple-400'
                }`} />
              </div>

              <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                {extractionFiles.length > 0 ? 'Add more PDF files' : 'Drag & drop PDF files here'}
              </p>

              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                or click to browse • Max 15 files
              </p>

              <button
                type="button"
                onClick={() => extractionFileInputRef.current.click()}
                className={`px-4 py-2 text-xs font-medium rounded-md transition-all ${
                  extractionFiles.length > 0
                    ? 'bg-green-100 hover:bg-green-200 text-green-700 dark:bg-green-900/30 dark:hover:bg-green-900/40 dark:text-green-300'
                    : 'bg-purple-100 hover:bg-purple-200 text-purple-700 dark:bg-purple-900/30 dark:hover:bg-purple-900/40 dark:text-purple-300'
                }`}
              >
                Select Files
              </button>
            </div>

            {/* Uploaded Files Button */}
            {extractionFiles.length > 0 && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setShowExtractionFiles(true)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 
                             bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 
                             transition-colors border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex items-center gap-3">
                    <FaEye className="text-gray-500" />
                    <span>View Uploaded Files ({extractionFiles.length})</span>
                  </div>
                  <FaCheckCircle className="text-green-500" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Middle and Right Columns - Section Definition and Table */}
      <div className="lg:col-span-2 space-y-6">
        {/* Section Definition Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <SectionDefinitionCard
            currentSection={currentSection}
            uploadedFiles={extractionFiles}
            sections={sections}
            editingSectionId={editingSectionId}
            titleOptions={titleOptions}
            periodTypeOptions={periodTypeOptions}
            onInputChange={handleInputChange}
            onCompanyInputChange={handleCompanyInputChange}
            onTickerInputChange={handleTickerInputChange}
            onTitleSelection={handleTitleSelection}
            onPeriodTypeSelection={handlePeriodTypeSelection}
            onCancelEdit={handleCancelEdit}
            onUpdateSection={handleUpdateSection}
            onAddSection={handleAddSection}
          />
        </div>

        {/* Sections Table Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <SectionsTableCard
            sections={sections}
            isProcessing={isProcessing}
            onEditSection={handleEditSection}
            onDeleteSection={handleDeleteSection}
            onClearSections={handleClearSections}
            onExtractData={handleExtractDataWrapper}
          />
        </div>
      </div>

      {/* Extraction Files Modal */}
      <ExtractionModal
        showExtractionFiles={showExtractionFiles}
        setShowExtractionFiles={setShowExtractionFiles}
        extractionFiles={extractionFiles}
        sections={sections}
        removeFile={handleRemoveFile}
      />
    </div>
  );
};

export default ExtractionFlow;